/**
 * Raffle Theme Context
 * Provides theme switching functionality for the raffle/lucky draw system
 */

import { createContext, useState, useEffect, ReactNode } from 'react';

export type RaffleTheme = 'vegas' | 'christmas';

interface RaffleThemeContextType {
  theme: RaffleTheme;
  setTheme: (theme: RaffleTheme) => void;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'raffle_theme';

export const RaffleThemeContext = createContext<RaffleThemeContextType | null>(null);

// Theme CSS variable definitions
const themeVariables: Record<RaffleTheme, Record<string, string>> = {
  vegas: {
    '--raffle-bg-gradient': 'linear-gradient(to bottom right, rgb(88, 28, 135), rgb(67, 56, 202), rgb(0, 0, 0))',
    '--raffle-bg-card': 'rgba(255, 255, 255, 0.1)',
    '--raffle-bg-card-hover': 'rgba(255, 255, 255, 0.2)',
    '--raffle-text-primary': 'rgb(251, 191, 36)',
    '--raffle-text-secondary': 'rgb(196, 181, 253)',
    '--raffle-text-accent': 'rgb(255, 255, 255)',
    '--raffle-border': 'rgba(167, 139, 250, 0.3)',
    '--raffle-accent': 'rgb(251, 191, 36)',
    '--raffle-accent-secondary': 'rgb(236, 72, 153)',
    '--raffle-glow': '0 0 20px rgba(251, 191, 36, 0.3)',
    '--raffle-header-gradient': 'linear-gradient(to right, rgb(251, 191, 36), rgb(253, 224, 71), rgb(251, 191, 36))',
    '--raffle-card-yellow': 'linear-gradient(to bottom right, rgb(234, 179, 8), rgb(202, 138, 4))',
    '--raffle-card-pink': 'linear-gradient(to bottom right, rgb(236, 72, 153), rgb(219, 39, 119))',
    '--raffle-card-cyan': 'linear-gradient(to bottom right, rgb(6, 182, 212), rgb(8, 145, 178))',
    '--raffle-card-green': 'linear-gradient(to bottom right, rgb(34, 197, 94), rgb(22, 163, 74))',
  },
  christmas: {
    '--raffle-bg-gradient': 'linear-gradient(to bottom right, rgb(127, 29, 29), rgb(21, 128, 61), rgb(6, 78, 59))',
    '--raffle-bg-card': 'rgba(255, 255, 255, 0.15)',
    '--raffle-bg-card-hover': 'rgba(255, 255, 255, 0.25)',
    '--raffle-text-primary': 'rgb(254, 202, 202)',
    '--raffle-text-secondary': 'rgb(187, 247, 208)',
    '--raffle-text-accent': 'rgb(255, 255, 255)',
    '--raffle-border': 'rgba(220, 38, 38, 0.4)',
    '--raffle-accent': 'rgb(239, 68, 68)',
    '--raffle-accent-secondary': 'rgb(34, 197, 94)',
    '--raffle-glow': '0 0 20px rgba(239, 68, 68, 0.4)',
    '--raffle-header-gradient': 'linear-gradient(to right, rgb(239, 68, 68), rgb(255, 255, 255), rgb(34, 197, 94))',
    '--raffle-card-yellow': 'linear-gradient(to bottom right, rgb(239, 68, 68), rgb(185, 28, 28))',
    '--raffle-card-pink': 'linear-gradient(to bottom right, rgb(34, 197, 94), rgb(22, 163, 74))',
    '--raffle-card-cyan': 'linear-gradient(to bottom right, rgb(220, 38, 38), rgb(153, 27, 27))',
    '--raffle-card-green': 'linear-gradient(to bottom right, rgb(21, 128, 61), rgb(22, 101, 52))',
  },
};

interface RaffleThemeProviderProps {
  children: ReactNode;
}

export function RaffleThemeProvider({ children }: RaffleThemeProviderProps) {
  const [theme, setThemeState] = useState<RaffleTheme>(() => {
    // Get initial theme from localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'vegas' || saved === 'christmas') {
        return saved;
      }
    } catch {
      // localStorage not available
    }
    return 'vegas';
  });

  // Apply theme CSS variables whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    const variables = themeVariables[theme];

    // Set data attribute for CSS selectors
    root.dataset.raffleTheme = theme;

    // Apply all CSS variables
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Persist to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      console.warn('Failed to save theme preference');
    }
  }, [theme]);

  const setTheme = (newTheme: RaffleTheme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState(current => current === 'vegas' ? 'christmas' : 'vegas');
  };

  return (
    <RaffleThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </RaffleThemeContext.Provider>
  );
}
